import { useEffect } from 'react';

import { useMapsLibrary } from '@vis.gl/react-google-maps';
import clsx from 'clsx';
import { ArrowDownUp, LoaderCircle } from 'lucide-react';

import { AutocompleteResults } from './AutocompleteResults';
import { AutocompleteSearch } from './AutocompleteSearch';
import { TravelTypeToggle } from './TravelTypeToggle';

import { PortalRenderer } from '@/components/PortalRenderer';
import { useConfigStore } from '@/store/ConfigStore';
import { useSearchStore } from '@/store/SearchStore';

export const LocationSearch = () => {
  const portalSelector = useConfigStore((state) => state.portals?.['travel-locations']);
  return (
    <PortalRenderer portalSelector={portalSelector}>
      <LocationSearchInternal />
    </PortalRenderer>
  );
};

const LocationSearchInternal = () => {
  const { searching, initializeLocations, selectedRoute } = useSearchStore();
  const { isMapExpanded } = useConfigStore();

  const places = useMapsLibrary('places');

  const onSwitchSelect = () => {
    const { locationStart, locationEnd } = useSearchStore.getState();
    const start = locationStart;
    const end = locationEnd;
    useSearchStore.setState({
      locationStart: end,
      locationEnd: start,
    });
  };

  useEffect(() => {
    if (places) {
      initializeLocations(places);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places]);

  if (selectedRoute) return null;

  const shouldHide = isMapExpanded && !selectedRoute;

  return (
    <div
      className={clsx(
        'lt-sm:gap-2 flex flex-col gap-4 transition-all duration-300 ease-in-out',
        shouldHide ? 'max-h-0 overflow-hidden opacity-0' : 'max-h-96 opacity-100',
      )}
    >
      <div className="relative flex flex-col gap-2">
        <AutocompleteSearch offset="start" />
        <AutocompleteSearch offset="end" />
        <div
          role="button"
          aria-label="Switch locations"
          className={clsx(
            'bg-border focus-visible:bg-secondary focus-visible:text-border focus-visible:outline-border absolute top-0 right-2 bottom-0 my-auto h-8 w-8 cursor-pointer rounded-sm p-1 text-white hover:bg-gray-500 focus-visible:outline-4 dark:bg-gray-950 dark:hover:bg-gray-700 focus-visible:dark:text-white',
            {
              '!bg-gray-500 dark:!bg-gray-700': searching,
            },
          )}
          tabIndex={0}
          onMouseDown={onSwitchSelect}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onSwitchSelect();
            }
          }}
        >
          {!searching && <ArrowDownUp />}
          {searching && <LoaderCircle className="animate-spin" />}
        </div>
      </div>
      <TravelTypeToggle />
      <AutocompleteResults />
    </div>
  );
};
